import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { CalendarDays, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CinematicVideo } from "@/components/CinematicVideo";

const INTRO_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/fpmbjpru_gemini_generated_video_1c503765.mp4";

export function OnThisDay() {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/public/on-this-day", { params: { refresh, lang: (i18n.language || "fr").slice(0, 2) } });
      setEvents(data.events || []);
    } catch (e) {
      if (refresh) toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.onThisDay.error"));
      setEvents(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(false); }, [i18n.language]); // eslint-disable-line

  const dateLabel = new Date().toLocaleDateString(i18n.language || "fr", { day: "numeric", month: "long" });

  return (
    <div data-testid="on-this-day">
      <div className="max-w-2xl mx-auto mb-7">
        <CinematicVideo fade noSound src={INTRO_VIDEO} testId="otd-cinematic-video" />
      </div>
      <div className="flex flex-col items-center text-center mb-7">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <CalendarDays className="text-primary pulse-star" size={22} strokeWidth={1.7} />
        </div>
        <p className="uppercase tracking-[0.25em] text-xs text-primary/80 mb-1.5">{t("atelier.onThisDay.title")}</p>
        <p className="font-display italic text-lg text-foreground/70">{dateLabel}</p>
      </div>

      {loading && !events ? (
        <p className="text-center font-display italic text-foreground/40 flex items-center justify-center gap-2 py-6">
          <Loader2 size={16} className="animate-spin" /> {t("atelier.onThisDay.loading")}
        </p>
      ) : events && events.length > 0 ? (
        <div className="space-y-3">
          {events.map((ev, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -14 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              data-testid={`otd-event-${i}`}
              className="flex gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4 hover:border-primary/25 transition-colors"
            >
              <span className="font-display text-primary text-lg sm:text-xl tabular-nums flex-shrink-0 w-16 text-right">{ev.year}</span>
              <div className="border-l border-primary/20 pl-4">
                <p className="font-display text-foreground/90 leading-tight mb-1">{ev.title}</p>
                <p className="text-sm text-foreground/55 leading-relaxed">{ev.detail}</p>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <p className="text-center font-display italic text-foreground/40">{t("atelier.onThisDay.error")}</p>
      )}

      <div className="text-center mt-6">
        <button
          onClick={() => load(true)} disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs text-foreground/45 hover:text-primary transition-colors disabled:opacity-50"
          data-testid="otd-refresh"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> {t("atelier.onThisDay.refresh")}
        </button>
      </div>
    </div>
  );
}
